function getSimpleSetBuilderNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatSimpleSetBuilderMoney(cents, moneyFormat) {
  const format = moneyFormat || '${{ amount }}';
  const placeholder = format.match(/{{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*}}/);
  if (!placeholder) return (cents / 100).toFixed(2);

  const formatType = placeholder[1];
  const useDecimals = !formatType.includes('no_decimals');
  const useCommaSeparator = formatType.includes('comma_separator');
  const amount = (cents / 100).toLocaleString(useCommaSeparator ? 'de-DE' : 'en-GB', {
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  });

  return format.replace(placeholder[0], amount);
}

if (!customElements.get('set-builder')) {
  customElements.define(
    'set-builder',
    class SimpleSetBuilder extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.modal = this.findModal();
        this.addEventListener('click', this.handleClick);
        this.modal?.addEventListener('click', this.handleClick);
        if (this.modal) {
          this.modalObserver = new MutationObserver(() => {
            if (!this.modal.hasAttribute('open')) this.unlockPageScroll();
          });
          this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
        }
        this.updateDisplay();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.modal?.removeEventListener('click', this.handleClick);
        this.modalObserver?.disconnect();
        this.unlockPageScroll();
      }

      findModal() {
        const openButton = this.querySelector('[data-simple-set-builder-open]');
        const modalId = openButton?.getAttribute('aria-controls');
        return this.querySelector('modal-dialog') || (modalId ? document.getElementById(modalId) : null);
      }

      lockPageScroll() {
        if (this.scrollLock) return;

        const body = document.body;
        this.scrollLock = {
          y: window.scrollY,
          position: body.style.position,
          top: body.style.top,
          width: body.style.width,
        };
        body.style.position = 'fixed';
        body.style.top = `-${this.scrollLock.y}px`;
        body.style.width = '100%';
      }

      unlockPageScroll() {
        if (!this.scrollLock) return;

        const { y, position, top, width } = this.scrollLock;
        document.body.style.position = position;
        document.body.style.top = top;
        document.body.style.width = width;
        this.scrollLock = null;
        window.scrollTo(0, y);
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-simple-set-builder-open]');
        if (openButton && this.contains(openButton)) {
          this.modal = this.modal || this.findModal();
          this.resetSubmissionState();
          this.modal?.show(openButton);
          if (this.modal?.hasAttribute('open')) this.lockPageScroll();
          return;
        }

        const quantityButton = event.target.closest(
          '[data-simple-set-builder-increment], [data-simple-set-builder-decrement]'
        );
        if (quantityButton && this.modal?.contains(quantityButton)) {
          event.preventDefault();

          const row = quantityButton.closest('[data-simple-set-builder-row]');
          if (!row || row.dataset.available !== 'true') return;

          const currentQuantity = getSimpleSetBuilderNumber(row.dataset.quantity);
          const change = quantityButton.hasAttribute('data-simple-set-builder-increment') ? 1 : -1;
          row.dataset.quantity = String(Math.min(99, Math.max(0, currentQuantity + change)));
          this.hideSelectionError();
          this.updateDisplay();
          return;
        }

        const addButton = event.target.closest('[data-simple-set-builder-add]');
        if (!addButton || !this.modal?.contains(addButton)) return;

        if (!this.getSelectedVariants().length) {
          this.showSelectionError();
          return;
        }

        const form = this.closest('product-info')?.querySelector('product-form form');
        if (!form) {
          console.error('Simple Set Builder could not find the product form.');
          return;
        }

        addButton.setAttribute('aria-disabled', 'true');
        addButton.classList.add('loading');
        this.dataset.simpleSetBuilderSubmitting = 'true';
        this.modal.hide();
        form.requestSubmit();
        queueMicrotask(() => delete this.dataset.simpleSetBuilderSubmitting);
      }

      getSelectedVariants() {
        return Array.from(this.getBuilderRoot().querySelectorAll('[data-simple-set-builder-row]'))
          .filter((row) => row.dataset.available === 'true' && getSimpleSetBuilderNumber(row.dataset.quantity) > 0)
          .map((row) => ({
            id: row.dataset.variantId,
            title: row.dataset.title || '',
            quantity: getSimpleSetBuilderNumber(row.dataset.quantity),
            price: getSimpleSetBuilderNumber(row.dataset.price),
          }));
      }

      isSubmissionActive() {
        return this.dataset.simpleSetBuilderSubmitting === 'true';
      }

      getCartItems(formData) {
        if (!this.isSubmissionActive()) return null;

        const selectedVariants = this.getSelectedVariants();
        if (!selectedVariants.length) return null;

        const parentQuantity = Math.max(1, getSimpleSetBuilderNumber(formData.get('quantity')) || 1);

        return selectedVariants.map((variant) => ({
          id: variant.id,
          quantity: variant.quantity * parentQuantity,
          properties: {
            _set_builder: 'true',
            _set_builder_title: variant.title,
            _set_builder_parent: this.dataset.parentTitle || '',
          },
        }));
      }

      updateDisplay() {
        const root = this.getBuilderRoot();
        const rows = Array.from(root.querySelectorAll('[data-simple-set-builder-row]'));
        const selectedVariants = this.getSelectedVariants();
        const selectedQuantity = selectedVariants.reduce((total, variant) => total + variant.quantity, 0);
        const selectedOptionsTotal = selectedVariants.reduce(
          (total, variant) => total + variant.price * variant.quantity,
          0
        );
        const subtotal = selectedOptionsTotal;

        rows.forEach((row) => {
          const quantity = getSimpleSetBuilderNumber(row.dataset.quantity);
          const decrement = row.querySelector('[data-simple-set-builder-decrement]');
          const increment = row.querySelector('[data-simple-set-builder-increment]');
          const quantityElement = row.querySelector('[data-simple-set-builder-quantity]');

          row.classList.toggle('is-selected', quantity > 0);
          if (quantityElement) quantityElement.textContent = String(quantity);
          if (decrement) decrement.disabled = quantity === 0 || row.dataset.available !== 'true';
          if (increment) increment.disabled = row.dataset.available !== 'true' || quantity >= 99;
        });

        const selection = root.querySelector('[data-simple-set-builder-selection]');
        if (selection) {
          const template = selectedQuantity === 1
            ? this.dataset.selectionOne || '[count] option selected'
            : this.dataset.selectionMany || '[count] options selected';
          selection.textContent = template.replaceAll('[count]', String(selectedQuantity));
        }

        const subtotalElement = root.querySelector('[data-simple-set-builder-subtotal]');
        if (subtotalElement) {
          subtotalElement.textContent = formatSimpleSetBuilderMoney(subtotal, this.dataset.moneyFormat);
        }

        const addButton = root.querySelector('[data-simple-set-builder-add]');
        if (addButton) {
          addButton.disabled = selectedVariants.length === 0;
          addButton.setAttribute('aria-disabled', String(selectedVariants.length === 0));
        }
      }

      resetSubmissionState() {
        const addButton = this.getBuilderRoot().querySelector('[data-simple-set-builder-add]');
        if (!addButton) return;

        addButton.removeAttribute('aria-disabled');
        addButton.classList.remove('loading');
      }

      showSelectionError() {
        const error = this.getBuilderRoot().querySelector('[data-simple-set-builder-error]');
        if (error) error.hidden = false;
      }

      hideSelectionError() {
        const error = this.getBuilderRoot().querySelector('[data-simple-set-builder-error]');
        if (error) error.hidden = true;
      }

      getBuilderRoot() {
        return this.modal || this;
      }
    }
  );
}
